'use client';

import { useMemo } from 'react';
import RespImage from '@/app/_components/core/resp-image';
import { SmartVideo } from '@/app/_components/core/smart-video';
import {
  CONTENT_VIDEO_LANDSCAPE_WIDTH,
  CONTENT_VIDEO_LANDSCAPE_HEIGHT,
  CONTENT_VIDEO_PORTRAIT_WIDTH,
  CONTENT_VIDEO_PORTRAIT_HEIGHT,
  ORIENTATION_LANDSCAPE,
} from '@/app/_components/shared/hero-advanced/hero-advanced.constants';
import type { ContentMediaProps } from '@/app/_components/shared/hero-advanced/hero-advanced.types';
import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import { buildResponsiveVideoSizesFromFiles } from '@/app/_lib/video-utils';

/**
 * Shared styles for halfsize media (image/video) in the hero.
 *
 * Aspect ratios:
 * - Portrait: 3:2 (landscape orientation image, wider than tall)
 * - Landscape: 3:4 (portrait orientation image, taller than wide)
 *
 * Max-height calculation:
 * - Uses calc(100svh - padding) to respect section-padding-y
 * - 8rem = py-16 (default), 7rem = py-14 (xl+)
 * - IMPORTANT: If section-padding-y changes in design-tokens, update these values!
 */
const HALFSIZE_MEDIA_BASE = [
  // Portrait mode (default)
  'aspect-3/2',
  'h-auto',
  'w-full',
  'rounded-hero-halfsize-media-radius',
  // Landscape mode
  'landscape:aspect-3/4',
  'landscape:max-h-[calc(100svh-8rem)]', // 100svh minus section-padding-y (py-16 = 4rem * 2)
  'landscape:xl:max-h-[calc(100svh-7rem)]', // xl+ uses py-14 = 3.5rem * 2
  'landscape:w-auto',
].join(' ');

/**
 * Image sizes for halfsize hero media with orientation-based art direction.
 * - Landscape screens: 3:4 portrait image (taller than wide)
 * - Portrait screens: 3:2 landscape image (wider than tall)
 * - Mobile fallback: viewport - container padding (16px * 2)
 */
const IMAGE_SIZES =
  '(orientation: landscape) and (min-width: 1920px) 712px, (orientation: landscape) and (min-width: 1280px) 559px, (orientation: landscape) and (min-width: 1024px) 386px, (orientation: portrait) and (min-width: 1024px) 992px, (orientation: portrait) and (min-width: 768px) 736px, (orientation: portrait) and (min-width: 640px) 608px, calc(100vw - 32px)';

export function ContentMedia({
  image,
  videoDesktop,
  videoMobile,
  priority,
}: ContentMediaProps) {
  // Memoize video sizes to prevent re-renders from causing video reload
  const videoSizes = useMemo(
    () =>
      buildResponsiveVideoSizesFromFiles(
        videoDesktop,
        videoMobile,
        { width: CONTENT_VIDEO_LANDSCAPE_WIDTH, height: CONTENT_VIDEO_LANDSCAPE_HEIGHT },
        { width: CONTENT_VIDEO_PORTRAIT_WIDTH, height: CONTENT_VIDEO_PORTRAIT_HEIGHT },
        ORIENTATION_LANDSCAPE
      ),
    [videoDesktop, videoMobile]
  );
  const autoPlay = videoDesktop?.[0]?.autoplay ?? videoMobile?.[0]?.autoplay ?? true;

  // Determine which media to show (image takes priority)
  const hasImage = Boolean(image);
  const hasVideo = videoSizes.length > 0;

  if (!hasImage && !hasVideo) {
    return null;
  }

  // Image transformation (only if image exists)
  // Backend portrait (tall) shows on landscape devices, backend landscape (wide) shows on portrait devices
  const imageProps = image
    ? (() => {
        const candidates = image.srcsetCandidates;
        if (Array.isArray(candidates)) {
          return transformSrcsetCandidates(candidates);
        }
        // Record format with art direction - swap keys for correct device mapping
        return transformSrcsetCandidates({
          fallback: candidates.landscape ?? candidates.portrait ?? [],
          variants: candidates.portrait
            ? [{ media: ORIENTATION_LANDSCAPE, srcsetCandidates: candidates.portrait }]
            : [],
        });
      })()
    : null;

  return (
    <div className="order-1 overflow-hidden landscape:order-2 landscape:ml-auto">
      {hasImage && imageProps ? (
        <RespImage
          srcsetCandidates={imageProps.srcsetCandidates}
          artDirection={imageProps.artDirection}
          alt={image?.altText ?? ''}
          title={emptyToUndefined(image?.title)}
          sizes={IMAGE_SIZES}
          className={cn(HALFSIZE_MEDIA_BASE, 'object-cover')}
          priority={priority}
        />
      ) : hasVideo ? (
        <SmartVideo
          lazy={false}
          videoProps={{
            className: cn(HALFSIZE_MEDIA_BASE, 'dark bg-background object-cover'),
            autoPlay,
            muted: autoPlay,
            loop: true,
            playsInline: true,
            controls: !autoPlay,
          }}
          pictureProps={{
            className: 'h-full w-full',
          }}
          imgProps={{
            alt: '',
            className: cn(HALFSIZE_MEDIA_BASE, 'landscape:object-cover'),
          }}
          sizes={videoSizes}
        />
      ) : null}
    </div>
  );
}